<template>
  <div>
    <b-row>
      <b-col xs="6" sm="4" md="4" lg="2" id="filter-controls">
        <h3 style="display: inline">{{ this.$t('message.filters') }}</h3>
        <b-button
          size="md"
          variant="outline-primary"
          style="float: right"
          @click="clearAllFilters()"
        >
          {{ this.$t('message.clear_all') }}</b-button
        >
        <br /><br />
        <b-form-group
          id="grouped-showInactive-form-group"
          :label="this.$t('message.projects')"
        >
          <b-form-checkbox
            id="grouped-showInactive-form-checkbox"
            v-model="showInactive"
            value="true"
            unchecked-value="false"
          >
            {{ this.$t('message.show_inactive_projects') }}
          </b-form-checkbox>
        </b-form-group>
        <b-form-group
          id="grouped-severity-form-group"
          :label="this.$t('message.severity')"
        >
          <b-form-checkbox-group
            id="grouped-severity-form-checkbox-group"
            v-model="severitySelected"
            :options="severityOptions"
            stacked
          >
          </b-form-checkbox-group>
        </b-form-group>
        <b-form-group
          id="grouped-publish-date-form-group"
          :label="this.$t('message.published')"
        >
          <b-datepicker
            id="grouped-publish-date-datepicker-from"
            v-model="publishDateFrom"
            :max="publishDateTo"
            :placeholder="this.$t('message.from')"
          ></b-datepicker>
          <b-datepicker
            id="grouped-publish-date-datepicker-to"
            v-model="publishDateTo"
            :min="publishDateFrom"
            :placeholder="this.$t('message.to')"
          ></b-datepicker>
        </b-form-group>
        <b-form-group
          id="grouped-text-search-form-group"
          :label="this.$t('message.text_search')"
        >
          <b-form-input
            id="grouped-text-search-form-input"
            v-model="textSearchInput"
            debounce="750"
            :placeholder="this.$t('message.search')"
          ></b-form-input>
          Search in:
          <b-form-checkbox-group
            id="grouped-text-search-form-checkbox-group"
            v-model="textSearchSelected"
            :options="textSearchOptions"
            stacked
          >
          </b-form-checkbox-group>
        </b-form-group>
        <b-form-group
          id="grouped-cvssv2-form-group"
          :label="this.$t('message.cvss_v2')"
        >
          <b-form-row>
            <b-col
              ><b-form-input
                id="grouped-cvssv2-from-form-input"
                v-model="cvssv2From"
                type="number"
                min="0"
                :max="cvssv2UpperLimit()"
                step="0.1"
                debounce="750"
                :state="cvssv2FromState()"
                :placeholder="this.$t('message.from')"
              ></b-form-input
            ></b-col>
            <b-col
              ><b-form-input
                id="grouped-cvssv2-to-form-input"
                v-model="cvssv2To"
                type="number"
                :min="cvssv2LowerLimit()"
                max="10"
                step="0.1"
                debounce="750"
                :state="cvssv2ToState()"
                :placeholder="this.$t('message.to')"
              ></b-form-input
            ></b-col>
          </b-form-row>
        </b-form-group>
        <b-form-group
          id="grouped-cvssv3-form-group"
          :label="this.$t('message.cvss_v3')"
        >
          <b-form-row>
            <b-col
              ><b-form-input
                id="grouped-cvssv3-from-form-input"
                v-model="cvssv3From"
                type="number"
                min="0"
                :max="cvssv3UpperLimit()"
                step="0.1"
                debounce="750"
                :state="cvssv3FromState()"
                :placeholder="this.$t('message.from')"
              ></b-form-input
            ></b-col>
            <b-col
              ><b-form-input
                id="grouped-cvssv3-to-form-input"
                v-model="cvssv3To"
                type="number"
                :min="cvssv3LowerLimit()"
                max="10"
                step="0.1"
                debounce="750"
                :state="cvssv3ToState()"
                :placeholder="this.$t('message.to')"
              ></b-form-input
            ></b-col>
          </b-form-row>
        </b-form-group>
        <b-form-group
          id="occurrences-form-group"
          :label="this.$t('message.occurrences_in_projects')"
        >
          <b-form-row>
            <b-col
              ><b-form-input
                id="occurrences-from-form-input"
                v-model="occurrencesFrom"
                type="number"
                min="0"
                :max="occurrencesUpperLimit()"
                step="1"
                debounce="750"
                :state="occurrencesFromState()"
                :placeholder="this.$t('message.from')"
              ></b-form-input
            ></b-col>
            <b-col
              ><b-form-input
                id="occurrences-to-form-input"
                v-model="occurrencesTo"
                type="number"
                :min="occurrencesLowerLimit()"
                step="1"
                debounce="750"
                :state="occurrencesToState()"
                :placeholder="this.$t('message.to')"
              ></b-form-input
            ></b-col>
          </b-form-row>
        </b-form-group>
      </b-col>
      <b-col xs="6" sm="8" md="8" lg="10">
        <bootstrap-table
          ref="table"
          :columns="columns"
          :data="data"
          :options="options"
          @on-load-success="onLoadSuccess"
        >
        </bootstrap-table>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import common from '@/shared/common';
import xssFilters from 'xss-filters';
import { loadUserPreferencesForBootstrapTable } from '@/shared/utils';

export default {
  computed: {
    watchers() {
      return [
        this.showInactive,
        this.severitySelected,
        this.publishDateFrom,
        this.publishDateTo,
      ];
    },
  },
  beforeMount() {
    this.initializeWatchers();
    this.textSearchSelected = this.textSearchOptions.map(
      (option) => option.value,
    );
  },
  methods: {
    initializeWatchers: function () {
      this.simpleWatcher = this.$watch('watchers', () => this.refreshTable());
      this.textSearchSelectedWatcher = this.$watch('textSearchSelected', () => {
        if (this.textSearchInput.trim().length !== 0) {
          this.refreshTable();
        }
      });
      this.textSearchInputWatcher = this.$watch(
        'textSearchInput',
        (newInput, oldInput) => {
          if (!(newInput.trim().length === 0 && oldInput.trim().length === 0)) {
            this.refreshTable();
          }
        },
      );
      this.cvssv2FromWatcher = this.$watch('cvssv2From', () => {
        if (this.cvssv2FromState() !== false) {
          this.refreshTable();
        }
      });
      this.cvssv2ToWatcher = this.$watch('cvssv2To', () => {
        if (this.cvssv2ToState() !== false) {
          this.refreshTable();
        }
      });
      this.cvssv3FromWatcher = this.$watch('cvssv3From', () => {
        if (this.cvssv3FromState() !== false) {
          this.refreshTable();
        }
      });
      this.cvssv3ToWatcher = this.$watch('cvssv3To', () => {
        if (this.cvssv3ToState() !== false) {
          this.refreshTable();
        }
      });
      this.occurrencesFromWatcher = this.$watch('occurrencesFrom', () => {
        if (this.occurrencesFromState() !== false) {
          this.refreshTable();
        }
      });
      this.occurrencesToWatcher = this.$watch('occurrencesTo', () => {
        if (this.occurrencesToState() !== false) {
          this.refreshTable();
        }
      });
    },
    apiUrl: function () {
      let url = `${this.$api.BASE_URL}/${this.$api.URL_FINDING}/grouped`;
      url +=
        '?showInactive=' +
        (this.showInactive === 'true') +
        '&showSuppressed=' +
        (this.showSuppressed === 'true');
      if (this.severitySelected && this.severitySelected.length > 0) {
        url += '&severity=' + this.severitySelected;
      }
      if (this.publishDateFrom && this.publishDateFrom.length > 0) {
        url += '&publishDateFrom=' + this.publishDateFrom;
      }
      if (this.publishDateTo && this.publishDateTo.length > 0) {
        url += '&publishDateTo=' + this.publishDateTo;
      }
      if (this.textSearchInput && this.textSearchInput.trim().length > 0) {
        url +=
          '&textSearchField=' +
          this.textSearchSelected +
          '&textSearchInput=' +
          this.textSearchInput.trim();
      }
      if (this.cvssv2From && this.cvssv2From.trim().length > 0) {
        url += '&cvssv2From=' + this.cvssv2From;
      }
      if (this.cvssv2To && this.cvssv2To.trim().length > 0) {
        url += '&cvssv2To=' + this.cvssv2To;
      }
      if (this.cvssv3From && this.cvssv3From.trim().length > 0) {
        url += '&cvssv3From=' + this.cvssv3From;
      }
      if (this.cvssv3To && this.cvssv3To.trim().length > 0) {
        url += '&cvssv3To=' + this.cvssv3To;
      }
      if (this.occurrencesFrom && this.occurrencesFrom.trim().length > 0) {
        url += '&occurrencesFrom=' + this.occurrencesFrom;
      }
      if (this.occurrencesTo && this.occurrencesTo.trim().length > 0) {
        url += '&occurrencesTo=' + this.occurrencesTo;
      }
      return url;
    },
    clearAllFilters: function () {
      this.simpleWatcher();
      this.textSearchSelectedWatcher();
      this.textSearchInputWatcher();
      this.cvssv2FromWatcher();
      this.cvssv2ToWatcher();
      this.cvssv3FromWatcher();
      this.cvssv3ToWatcher();
      this.occurrencesFromWatcher();
      this.occurrencesToWatcher();
      this.showInactive = false;
      this.severitySelected = [];
      this.publishDateFrom = '';
      this.publishDateTo = '';
      this.textSearchInput = '';
      this.textSearchSelected = this.textSearchOptions.map(
        (option) => option.value,
      );
      this.cvssv2From = '';
      this.cvssv2To = '';
      this.cvssv3From = '';
      this.cvssv3To = '';
      this.occurrencesFrom = '';
      this.occurrencesTo = '';
      this.refreshTable();
      this.initializeWatchers();
    },
    refreshTable: function () {
      this.$refs.table.refresh({
        url: this.apiUrl(),
        silent: true,
        pageNumber: 1,
      });
    },
    cvssv2UpperLimit: function () {
      return this.cvssv2To ? this.cvssv2To : 10;
    },
    cvssv2LowerLimit: function () {
      return this.cvssv2From ? this.cvssv2From : 0;
    },
    cvssv2FromState() {
      return this.cvssv2From
        ? !isNaN(this.cvssv2From) &&
            (!this.cvssv2To ||
              !isNaN(this.cvssv2To) ||
              this.cvssv2From <= this.cvssv2To) &&
            this.cvssv2From <= 10
        : null;
    },
    cvssv2ToState() {
      return this.cvssv2To
        ? !isNaN(this.cvssv2To) &&
            (!this.cvssv2From ||
              !isNaN(this.cvssv2From) ||
              this.cvssv2To >= this.cvssv2From) &&
            this.cvssv2To >= 0
        : null;
    },
    cvssv3UpperLimit: function () {
      return this.cvssv3To ? this.cvssv3To : 10;
    },
    cvssv3LowerLimit: function () {
      return this.cvssv3From ? this.cvssv3From : 0;
    },
    cvssv3FromState() {
      return this.cvssv3From
        ? !isNaN(this.cvssv3From) &&
            (!this.cvssv3To ||
              !isNaN(this.cvssv3To) ||
              this.cvssv3From <= this.cvssv3To) &&
            this.cvssv3From <= 10
        : null;
    },
    cvssv3ToState() {
      return this.cvssv3To
        ? !isNaN(this.cvssv3To) &&
            (!this.cvssv3From ||
              !isNaN(this.cvssv3From) ||
              this.cvssv3To >= this.cvssv3From) &&
            this.cvssv3To >= 0
        : null;
    },
    occurrencesUpperLimit: function () {
      return this.occurrencesTo ? this.occurrencesTo : null;
    },
    occurrencesLowerLimit: function () {
      return this.occurrencesFrom ? this.occurrencesFrom : 0;
    },
    occurrencesFromState: function () {
      return this.occurrencesFrom
        ? !isNaN(this.occurrencesFrom) &&
            (!this.occurrencesTo ||
              !isNaN(this.occurrencesTo) ||
              this.occurrencesFrom <= this.occurrencesTo) &&
            this.cvssv3From >= 0
        : null;
    },
    occurrencesToState: function () {
      return this.occurrencesTo
        ? !isNaN(this.occurrencesTo) &&
            (!this.occurrencesFrom ||
              !isNaN(this.occurrencesFrom) ||
              this.occurrencesTo >= this.occurrencesFrom) &&
            this.cvssv3To >= 0
        : null;
    },
    onLoadSuccess: function () {
      loadUserPreferencesForBootstrapTable(
        this,
        'VulnerabilityAuditGroupedByVulnerability',
        this.$refs.table.columns,
      );
    },
  },
  data() {
    return {
      simpleWatcher: null,
      textSearchSelectedWatcher: null,
      textSearchInputWatcher: null,
      cvssv2FromWatcher: null,
      cvssv2ToWatcher: null,
      cvssv3FromWatcher: null,
      cvssv3ToWatcher: null,
      occurrencesFromWatcher: null,
      occurrencesToWatcher: null,
      showInactive: false,
      severitySelected: [],
      severityOptions: [
        { text: 'Critical', value: 'critical' },
        { text: 'High', value: 'high' },
        { text: 'Medium', value: 'medium' },
        { text: 'Low', value: 'low' },
        { text: 'Unassigned', value: 'unassigned' },
      ],
      publishDateFrom: '',
      publishDateTo: '',
      textSearchInput: '',
      textSearchOptions: [
        { text: 'Vulnerability ID', value: 'vulnerability_id' },
        { text: 'Vulnerability Title', value: 'vulnerability_title' },
        { text: 'Component Name', value: 'component_name' },
        { text: 'Component Version', value: 'component_version' },
        { text: 'Project Name', value: 'project_name' },
      ],
      textSearchSelected: [],
      cvssv2From: '',
      cvssv2To: '',
      cvssv3From: '',
      cvssv3To: '',
      occurrencesFrom: '',
      occurrencesTo: '',
      columns: [
        {
          title: this.$t('message.vulnerability'),
          field: 'vulnerability.vulnId',
          sortable: true,
          formatter(value, row, index) {
            let url = xssFilters.uriInUnQuotedAttr(
              '../vulnerabilities/' +
                row.vulnerability.source +
                '/' +
                encodeURIComponent(value),
            );
            return (
              common.formatSourceLabel(row.vulnerability.source) +
              ` <a href="${url}">${xssFilters.inHTMLData(value)}</a>`
            );
          },
        },
        {
          title: this.$t('message.title'),
          field: 'vulnerability.title',
          sortable: true,
        },
        {
          title: this.$t('message.severity'),
          field: 'vulnerability.severity',
          sortable: true,
          formatter(value, row, index) {
            if (typeof value !== 'undefined') {
              return common.formatSeverityLabel(value);
            }
          },
        },
        {
          title: this.$t('message.analyzer'),
          field: 'attribution.analyzerIdentity',
          sortable: true,
          formatter(value, row, index) {
            return common.formatAnalyzerLabel(
              row.attribution.analyzerIdentity,
              row.vulnerability.source,
              row.vulnerability.vulnId,
              row.attribution.alternateIdentifier,
              row.attribution.referenceUrl,
            );
          },
        },
        {
          title: this.$t('message.published'),
          field: 'vulnerability.published',
          sortable: true,
          formatter(value, row, index) {
            if (typeof value !== 'undefined') {
              return common.formatTimestamp(value);
            }
          },
        },
        {
          title: this.$t('message.cwe'),
          field: 'vulnerability.cwes',
          sortable: false,
          formatter(value, row, index) {
            if (typeof value !== 'undefined') {
              let s = '';
              for (let i = 0; i < value.length; i++) {
                let cwe = value[i];
                if (i > 0) {
                  s += ',&nbsp;&nbsp;&nbsp;';
                }
                s += `CWE-${cwe.cweId}`;
              }
              return s;
            }
          },
        },
        {
          title: this.$t('message.cvss_v2'),
          field: 'vulnerability.cvssV2BaseScore',
          sortable: true,
          formatter(value, row, index) {
            if (value && typeof value === 'number') {
              return value.toFixed(1);
            } else {
              return null;
            }
          },
        },
        {
          title: this.$t('message.cvss_v3'),
          field: 'vulnerability.cvssV3BaseScore',
          sortable: true,
          formatter(value, row, index) {
            if (value && typeof value === 'number') {
              return value.toFixed(1);
            } else {
              return null;
            }
          },
        },
        {
          title: 'Projects',
          field: 'vulnerability.affectedProjectCount',
          sortable: true,
        },
      ],
      data: [],
      options: {
        search: false,
        showColumns: true,
        showRefresh: true,
        pagination: true,
        silentSort: false,
        sidePagination: 'server',
        queryParamsType: 'pageSize',
        pageList: '[10, 25, 50, 100]',
        pageSize:
          localStorage &&
          localStorage.getItem(
            'VulnerabilityAuditGroupedByVulnerabilityPageSize',
          ) !== null
            ? Number(
                localStorage.getItem(
                  'VulnerabilityAuditGroupedByVulnerabilityPageSize',
                ),
              )
            : 10,
        sortName:
          localStorage &&
          localStorage.getItem(
            'VulnerabilityAuditGroupedByVulnerabilitySortName',
          ) !== null
            ? localStorage.getItem(
                'VulnerabilityAuditGroupedByVulnerabilitySortName',
              )
            : 'vulnerability.affectedProjectCount',
        sortOrder:
          localStorage &&
          localStorage.getItem(
            'VulnerabilityAuditGroupedByVulnerabilitySortOrder',
          ) !== null
            ? localStorage.getItem(
                'VulnerabilityAuditGroupedByVulnerabilitySortOrder',
              )
            : 'desc',
        icons: {
          refresh: 'fa-refresh',
        },
        toolbar: '#vulnerabilitiesToolbar',
        responseHandler: function (res, xhr) {
          res.total = xhr.getResponseHeader('X-Total-Count');
          return res;
        },
        url: this.apiUrl(),
        onPageChange: (number, size) => {
          if (localStorage) {
            localStorage.setItem(
              'VulnerabilityAuditGroupedByVulnerabilityPageSize',
              size.toString(),
            );
          }
        },
        onColumnSwitch: (field, checked) => {
          if (localStorage) {
            localStorage.setItem(
              'VulnerabilityAuditGroupedByVulnerabilityShow' +
                common.capitalize(field),
              checked.toString(),
            );
          }
        },
        onSort: (name, order) => {
          if (localStorage) {
            localStorage.setItem(
              'VulnerabilityAuditGroupedByVulnerabilitySortName',
              name,
            );
            localStorage.setItem(
              'VulnerabilityAuditGroupedByVulnerabilitySortOrder',
              order,
            );
          }
        },
      },
    };
  },
};
</script>
